import React, { useState, useMemo } from "react";
import { User, Plus, X, Check, Info } from "lucide-react";
import { INK, INK_2, INK_3, LINE, BURGUNDY } from "../../Bureau/bureauTokens";
import { useEtatGlobal } from "../../EtatGlobal.js";
import { httpClient } from "../../../HttpClient";
import { normalizeText } from "../../../utils/text.js";

function initials(u) {
  if (!u) return "?";
  return `${(u.name || "")[0] || ""}${(u.lastName || "")[0] || ""}`.toUpperCase();
}

// Onglet "Partage" — partage réel du planning interne de l'utilisateur
// courant : que l'accès accordé soit "lecture" ou "modification", un
// collègue partagé voit mes événements dans sa grille fusionnée (contrôle
// serveur dans getEvenementsForPlanning) — les deux niveaux ont
// aujourd'hui le même effet réel, "modification" n'accorde pour l'instant
// aucun droit de création supplémentaire (assigner un collaborateur à une
// "Demande de rdv" reste ouvert à tous, comme avant, pour ne pas casser le
// formulaire Événements classique). `shares`/`onChange` viennent de
// PlanningSettingsModal (GET /planning-shares).
function SharePlanningPanel({ shares, onChange }) {
  const { users, utilisateur } = useEtatGlobal();
  const [q, setQ] = useState("");

  const shareOf = (granteeId) => shares.find((s) => s.granteeId === granteeId);

  const matches = useMemo(() => {
    const query = normalizeText(q);
    return (users || [])
      .filter((u) => u.id !== utilisateur?.id)
      .filter((u) => !query || normalizeText(`${u.name} ${u.lastName}`).includes(query))
      .slice(0, 40);
  }, [users, utilisateur, q]);

  const setAccess = async (granteeId, access) => {
    await httpClient.post("/api/planning-shares", { granteeId, access });
    await onChange();
  };

  const removeShare = async (shareId) => {
    await httpClient.delete(`/api/planning-shares/${shareId}`);
    await onChange();
  };

  return (
    <>
      <div className="mb-3" style={{ background: "#FFF6F8", border: "1px solid #E8C8D2", borderRadius: 10, padding: "12px 14px" }}>
        <div className="flex items-center gap-1.5 text-xs font-semibold mb-2" style={{ color: BURGUNDY }}>
          <User size={13} /> Membres ayant accès <span style={{ color: INK_3, fontWeight: 600 }}>({shares.length})</span>
        </div>
        {shares.length === 0 && (
          <p className="text-xs" style={{ color: INK_3 }}>
            Planning non partagé pour l'instant.
          </p>
        )}
        <div className="flex flex-col gap-1.5">
          {shares.map((s) => (
            <div key={s.id} className="flex items-center gap-2 px-2.5 py-2" style={{ border: `1px solid ${LINE}`, borderRadius: 8, background: "#fff" }}>
              <span
                className="flex items-center justify-center text-white font-bold flex-shrink-0"
                style={{ width: 24, height: 24, borderRadius: "50%", background: BURGUNDY, fontSize: 10 }}
              >
                {initials(s.grantee)}
              </span>
              <span className="text-xs font-semibold flex-1 truncate" style={{ color: INK }}>
                {s.grantee?.name} {s.grantee?.lastName}
              </span>
              <div className="flex flex-shrink-0" style={{ border: `1px solid ${LINE}`, borderRadius: 9, overflow: "hidden" }}>
                <button
                  onClick={() => setAccess(s.granteeId, "lecture")}
                  className="text-[10.5px] font-semibold px-2.5 py-1"
                  style={{ background: s.access === "lecture" ? BURGUNDY : "#fff", color: s.access === "lecture" ? "#fff" : INK_2 }}
                >
                  Lecture
                </button>
                <button
                  onClick={() => setAccess(s.granteeId, "modification")}
                  className="text-[10.5px] font-semibold px-2.5 py-1"
                  style={{ background: s.access === "modification" ? BURGUNDY : "#fff", color: s.access === "modification" ? "#fff" : INK_2, borderLeft: `1px solid ${LINE}` }}
                >
                  Modification
                </button>
              </div>
              <button onClick={() => removeShare(s.id)} title="Retirer l'accès" className="flex items-center justify-center flex-shrink-0" style={{ width: 24, height: 24, borderRadius: "50%", color: INK_3 }}>
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: "#FAFAF8", border: `1px solid ${LINE}`, borderRadius: 10, padding: "12px 14px" }}>
        <div className="flex items-center gap-1.5 text-xs font-semibold mb-2" style={{ color: BURGUNDY }}>
          <Plus size={13} /> Ajouter un membre
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher un collaborateur…"
          className="w-full h-9 px-3 text-xs outline-none mb-2.5"
          style={{ border: `1px solid ${LINE}`, borderRadius: 8, color: INK, background: "#fff" }}
        />
        <div className="flex flex-col gap-1 max-h-56 overflow-y-auto">
          {matches.map((u) => {
            const s = shareOf(u.id);
            return (
              <button
                key={u.id}
                onClick={() => (s ? removeShare(s.id) : setAccess(u.id, "lecture"))}
                className="flex items-center gap-2 px-2.5 py-2 text-left"
                style={{ border: `1px solid ${s ? BURGUNDY : LINE}`, borderRadius: 8, background: s ? "#FFF6F8" : "#fff" }}
              >
                <span
                  className="flex items-center justify-center text-white font-bold flex-shrink-0"
                  style={{ width: 22, height: 22, borderRadius: "50%", background: BURGUNDY, fontSize: 9.5 }}
                >
                  {initials(u)}
                </span>
                <span className="text-xs font-semibold flex-1 truncate" style={{ color: INK }}>
                  {u.name} {u.lastName}
                </span>
                {s ? (
                  <span className="flex items-center gap-1 text-[10.5px] font-semibold" style={{ color: BURGUNDY }}>
                    <Check size={12} /> Ajouté
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-[10.5px]" style={{ color: INK_3 }}>
                    <Plus size={12} /> Ajouter
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1.5 mt-2.5 text-[11px]" style={{ color: INK_3 }}>
          <Info size={12} /> Un membre partagé (Lecture ou Modification) voit vos événements dans son planning fusionné.
        </div>
      </div>
    </>
  );
}

export default SharePlanningPanel;
